/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package cmp.navigation.authenticatednavbar

import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.material3.SnackbarDuration.Indefinite
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.NavDestination.Companion.hierarchy
import androidx.navigation.NavGraph.Companion.findStartDestination
import androidx.navigation.NavHostController
import androidx.navigation.NavOptions
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.navOptions
import cmp.navigation.generated.resources.Res
import cmp.navigation.generated.resources.not_connected
import cmp.navigation.generated.resources.notification_permission_description
import cmp.navigation.generated.resources.notification_permission_enable
import cmp.navigation.generated.resources.notification_permission_not_now
import cmp.navigation.generated.resources.notification_permission_title
import cmp.navigation.ui.KptRootScaffold
import cmp.navigation.ui.ScaffoldNavigationData
import cmp.navigation.ui.logDestinationChanged
import cmp.navigation.ui.rememberKptNavController
import kotlinx.collections.immutable.persistentListOf
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import studio.oddpods.cauce.core.base.analytics.rememberAnalyticsHelper
import studio.oddpods.cauce.core.base.platform.notification.NotificationPermissionCheckpoint
import studio.oddpods.cauce.core.base.ui.EventsEffect
import studio.oddpods.cauce.core.base.ui.RootTransitionProviders
import studio.oddpods.cauce.core.ui.NavigationItem
import studio.oddpods.cauce.feature.dashboard.DashboardDestination
import studio.oddpods.cauce.feature.dashboard.dashboardGraph
import studio.oddpods.cauce.feature.dashboard.navigateToDashboard
import studio.oddpods.cauce.feature.patients.navigateToPatientDetail
import studio.oddpods.cauce.feature.patients.navigateToPatients
import studio.oddpods.cauce.feature.patients.patientsGraph
import studio.oddpods.cauce.feature.settings.navigateToSettings
import studio.oddpods.cauce.feature.settings.settingsTabGraph

@Composable
internal fun AuthenticatedNavbarNavigationScreen(
    modifier: Modifier = Modifier,
    navController: NavHostController = rememberKptNavController(
        name = "AuthenticatedNavbarScreen",
    ),
    viewModel: AuthenticatedNavbarNavigationViewModel = koinViewModel(),
    onLogSession: (String) -> Unit = {},
    onNavigateToReminders: () -> Unit = {},
    onNavigateToNotifications: () -> Unit = {},
    onOpenReminders: () -> Unit = {},
    onSendReminder: (String) -> Unit = {},
    onCreateRecurringSchedule: (String) -> Unit = {},
    onEditRecurringSchedule: (String, String) -> Unit = { _, _ -> },
    onSessionClick: (String) -> Unit = {},
) {
    val analyticsHelper = rememberAnalyticsHelper()
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val isOffline by viewModel.isOffline.collectAsStateWithLifecycle()
    var shouldRequestNotificationPermission by rememberSaveable { mutableStateOf(true) }

    NotificationPermissionCheckpoint(
        shouldRequest = shouldRequestNotificationPermission,
        title = stringResource(Res.string.notification_permission_title),
        description = stringResource(Res.string.notification_permission_description),
        confirmText = stringResource(Res.string.notification_permission_enable),
        dismissText = stringResource(Res.string.notification_permission_not_now),
        onResult = { shouldRequestNotificationPermission = false },
        onDismiss = { shouldRequestNotificationPermission = false },
    ) {}

    EventsEffect(eventFlow = viewModel.eventFlow) { event ->
        navController.apply {
            when (event) {
                AuthenticatedNavBarEvent.NavigateToDashboard -> {
                    analyticsHelper.logDestinationChanged(event.tab.startDestinationRoute)
                    navigateToTabOrRoot(tabToNavigateTo = event.tab) {
                        navigateToDashboard(navOptions = it)
                    }
                }

                AuthenticatedNavBarEvent.NavigateToPatients -> {
                    analyticsHelper.logDestinationChanged(event.tab.startDestinationRoute)
                    navigateToTabOrRoot(tabToNavigateTo = event.tab) {
                        navigateToPatients(navOptions = it)
                    }
                }

                AuthenticatedNavBarEvent.NavigateToSettings -> {
                    analyticsHelper.logDestinationChanged(event.tab.startDestinationRoute)
                    navigateToTabOrRoot(tabToNavigateTo = event.tab) {
                        navigateToSettings(navOptions = it)
                    }
                }
            }
        }
    }

    val message = stringResource(Res.string.not_connected)
    LaunchedEffect(isOffline) {
        if (isOffline) {
            scope.launch {
                snackbarHostState.showSnackbar(
                    message = message,
                    duration = Indefinite,
                )
            }
        }
    }

    AuthenticatedNavbarNavigationScreenContent(
        navController = navController,
        snackbarHostState = snackbarHostState,
        modifier = modifier,
        onLogSession = onLogSession,
        onNavigateToReminders = onNavigateToReminders,
        onNavigateToNotifications = onNavigateToNotifications,
        onOpenReminders = onOpenReminders,
        onSendReminder = onSendReminder,
        onCreateRecurringSchedule = onCreateRecurringSchedule,
        onEditRecurringSchedule = onEditRecurringSchedule,
        onSessionClick = onSessionClick,
        onAction = remember(viewModel) {
            { viewModel.trySendAction(it) }
        },
    )
}

@Composable
internal fun AuthenticatedNavbarNavigationScreenContent(
    navController: NavHostController,
    modifier: Modifier = Modifier,
    onLogSession: (String) -> Unit = {},
    onNavigateToReminders: () -> Unit = {},
    onNavigateToNotifications: () -> Unit = {},
    onOpenReminders: () -> Unit = {},
    onSendReminder: (String) -> Unit = {},
    onCreateRecurringSchedule: (String) -> Unit = {},
    onEditRecurringSchedule: (String, String) -> Unit = { _, _ -> },
    onSessionClick: (String) -> Unit = {},
    snackbarHostState: SnackbarHostState = remember { SnackbarHostState() },
    onAction: (AuthenticatedNavBarAction) -> Unit,
) {
    val navBackStackEntry by navController.currentBackStackEntryAsState()
    val navigationItems = persistentListOf<NavigationItem>(
        AuthenticatedNavBarTabItem.DashboardTab,
        AuthenticatedNavBarTabItem.PatientsTab,
        AuthenticatedNavBarTabItem.SettingsTab,
    )

    KptRootScaffold(
        contentWindowInsets = WindowInsets(0.dp),
        navigationData = ScaffoldNavigationData(
            navigationItems = navigationItems,
            selectedNavigationItem = navigationItems.find {
                navBackStackEntry.isCurrentRoute(route = it.graphRoute)
            },
            onNavigationClick = { navigationItem ->
                when (navigationItem) {
                    is AuthenticatedNavBarTabItem.DashboardTab -> {
                        onAction(AuthenticatedNavBarAction.DashboardTabClick)
                    }

                    is AuthenticatedNavBarTabItem.PatientsTab -> {
                        onAction(AuthenticatedNavBarAction.PatientsTabClick)
                    }

                    is AuthenticatedNavBarTabItem.SettingsTab -> {
                        onAction(AuthenticatedNavBarAction.SettingsTabClick)
                    }
                }
            },
            shouldShowNavigation = navigationItems.any {
                navBackStackEntry.isCurrentRoute(route = it.startDestinationRoute)
            },
        ),
        snackbarHost = {
            SnackbarHost(hostState = snackbarHostState)
        },
        modifier = modifier,
    ) {
        NavHost(
            navController = navController,
            startDestination = DashboardDestination,
            enterTransition = RootTransitionProviders.Enter.fadeIn,
            exitTransition = RootTransitionProviders.Exit.fadeOut,
            popEnterTransition = RootTransitionProviders.Enter.fadeIn,
            popExitTransition = RootTransitionProviders.Exit.fadeOut,
        ) {
            dashboardGraph(
                navController = navController,
                onPatientClick = { patientId -> navController.navigateToPatientDetail(patientId) },
                onOpenReminders = onOpenReminders,
            )

            patientsGraph(
                navController = navController,
                onPatientClick = { patientId -> navController.navigateToPatientDetail(patientId) },
                onLogSession = onLogSession,
                onSendReminder = onSendReminder,
                onCreateRecurringSchedule = onCreateRecurringSchedule,
                onEditRecurringSchedule = onEditRecurringSchedule,
                onSessionClick = onSessionClick,
            )

            settingsTabGraph(
                onNavigateToReminders = onNavigateToReminders,
                onNavigateToNotifications = onNavigateToNotifications,
            )
        }
    }
}

private fun NavController.navigateToTabOrRoot(
    tabToNavigateTo: AuthenticatedNavBarTabItem,
    navigate: (NavOptions) -> Unit,
) {
    if (tabToNavigateTo.startDestinationRoute == currentDestination?.route) {
        return
    } else if (currentDestination?.parent?.route == tabToNavigateTo.graphRoute) {
        popBackStack(route = tabToNavigateTo.startDestinationRoute, inclusive = false)
    } else {
        navigate(
            navOptions {
                popUpTo(graph.findStartDestination().id) {
                    saveState = true
                }
                launchSingleTop = true
                restoreState = true
            },
        )
    }
}

private fun NavBackStackEntry?.isCurrentRoute(route: String): Boolean =
    this
        ?.destination
        ?.hierarchy
        ?.any { it.route == route } == true
